import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';
import { getLanguage } from '@deadpan/shared';

export const UndercoverWordsmithWordInput: React.FC = () => {
  const { socket, gameState, currentRoom } = useSocket();
  const t = useTranslations();
  const [lang, setLang] = React.useState<'en' | 'zh'>(getLanguage());
  const [civilianWord, setCivilianWord] = React.useState('');
  const [undercoverWord, setUndercoverWord] = React.useState('');
  const [civilianWordZh, setCivilianWordZh] = React.useState('');
  const [undercoverWordZh, setUndercoverWordZh] = React.useState('');
  const [blankHint, setBlankHint] = React.useState('');
  const [blankHintZh, setBlankHintZh] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Check if there are blank players
  const hasBlankPlayers = (currentRoom?.undercoverConfig?.blankCount || 0) > 0;

  // Listen for language changes
  React.useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);

  const isSubmitted = gameState?.wordsmithSubmitted || false;

  // Reset isSubmitting when submission is confirmed, phase changes, or words are set
  React.useEffect(() => {
    if (isSubmitted || currentRoom?.phase !== 'wordsmith_input' || (gameState?.wordsmithCivilianWord && gameState?.wordsmithUndercoverWord)) {
      setIsSubmitting(false);
    }
  }, [isSubmitted, currentRoom?.phase, gameState?.wordsmithCivilianWord, gameState?.wordsmithUndercoverWord]);

  const handleSubmit = () => {
    // Get words based on current language
    const currentCivilianWord = lang === 'zh' ? civilianWordZh : civilianWord;
    const currentUndercoverWord = lang === 'zh' ? undercoverWordZh : undercoverWord;
    const currentBlankHint = lang === 'zh' ? blankHintZh : blankHint;
    
    if (!socket || !currentCivilianWord.trim() || !currentUndercoverWord.trim() || isSubmitting || isSubmitted) return;
    
    setIsSubmitting(true);
    // Submit both civilian and undercover words
    // Parameters: (primaryCivilianWord, primaryUndercoverWord, secondaryCivilianWord?, secondaryUndercoverWord?, primaryBlankHint?, secondaryBlankHint?)
    // If Chinese is primary, send Chinese first, then English
    // If English is primary, send English first, then Chinese
    if (lang === 'zh') {
      socket.emit('client:submit-word', 
        currentCivilianWord.trim(), // Chinese civilian word (primary)
        currentUndercoverWord.trim(), // Chinese undercover word (primary)
        civilianWord.trim() || undefined, // English civilian word (secondary)
        undercoverWord.trim() || undefined, // English undercover word (secondary)
        hasBlankPlayers ? (currentBlankHint.trim() || undefined) : undefined, // Chinese blank hint (primary)
        hasBlankPlayers ? (blankHint.trim() || undefined) : undefined // English blank hint (secondary)
      );
    } else {
      socket.emit('client:submit-word', 
        currentCivilianWord.trim(), // English civilian word (primary)
        currentUndercoverWord.trim(), // English undercover word (primary)
        civilianWordZh.trim() || undefined, // Chinese civilian word (secondary)
        undercoverWordZh.trim() || undefined, // Chinese undercover word (secondary)
        hasBlankPlayers ? (currentBlankHint.trim() || undefined) : undefined, // English blank hint (primary)
        hasBlankPlayers ? (blankHintZh.trim() || undefined) : undefined // Chinese blank hint (secondary)
      );
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-3 sm:mb-4 px-2">
            {t.games.undercover.wordsmithInput || '谁是卧底 - 词汇输入'}
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-2 px-2">
            {t.games.undercover.wordsmithSubmitted || '词汇已提交！'}
          </p>
          {gameState?.wordsmithCivilianWord && gameState?.wordsmithUndercoverWord && (
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">{t.games.undercover.civilianWord || '平民词汇'}:</p>
                <div className="w-full overflow-x-auto">
                  <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">
                    {lang === 'zh' && gameState.wordsmithCivilianWordZh ? gameState.wordsmithCivilianWordZh : gameState.wordsmithCivilianWord}
                  </p>
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">{t.games.undercover.undercoverWord || '卧底词汇'}:</p>
                <div className="w-full overflow-x-auto">
                  <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">
                    {lang === 'zh' && gameState.wordsmithUndercoverWordZh ? gameState.wordsmithUndercoverWordZh : gameState.wordsmithUndercoverWord}
                  </p>
                </div>
              </div>
              {hasBlankPlayers && (gameState.wordsmithBlankHint || gameState.wordsmithBlankHintZh) && (
                <div className="p-3 sm:p-4 bg-yellow-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">{t.games.undercover.blankHint || '白板提示'}:</p>
                  <div className="w-full overflow-x-auto">
                    <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">
                      {lang === 'zh' && gameState.wordsmithBlankHintZh ? gameState.wordsmithBlankHintZh : gameState.wordsmithBlankHint}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.undercover.wordsmithInput || '谁是卧底 - 词汇输入'}
        </h2>
        <p className="text-base sm:text-lg text-gray-700 mb-3 sm:mb-4 px-2">
          {t.games.undercover.wordsmithInstructions || '请为平民和卧底阵营分别提供一个词汇。这两个词汇应该相似但不完全相同，以增加游戏的挑战性。'}
        </p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            {t.games.undercover.civilianWord || '平民词汇'}
          </label>
          <input
            type="text"
            value={lang === 'zh' ? civilianWordZh : civilianWord}
            onChange={(e) => {
              if (lang === 'zh') {
                setCivilianWordZh(e.target.value);
              } else {
                setCivilianWord(e.target.value);
              }
            }}
            placeholder={lang === 'zh' ? '输入平民词汇' : 'Enter civilian word'}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none text-base sm:text-lg"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            {t.games.undercover.undercoverWord || '卧底词汇'}
          </label>
          <input
            type="text"
            value={lang === 'zh' ? undercoverWordZh : undercoverWord}
            onChange={(e) => {
              if (lang === 'zh') {
                setUndercoverWordZh(e.target.value);
              } else {
                setUndercoverWord(e.target.value);
              }
            }}
            placeholder={lang === 'zh' ? '输入卧底词汇' : 'Enter undercover word'}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none text-base sm:text-lg"
            disabled={isSubmitting}
          />
        </div>

        {hasBlankPlayers && (
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              {t.games.undercover.blankHint || '白板提示'} {lang === 'zh' ? '(可选)' : '(Optional)'}
            </label>
            <input
              type="text"
              value={lang === 'zh' ? blankHintZh : blankHint}
              onChange={(e) => {
                if (lang === 'zh') {
                  setBlankHintZh(e.target.value);
                } else {
                  setBlankHint(e.target.value);
                }
              }}
              placeholder={lang === 'zh' ? '输入白板提示（可选）' : 'Enter blank hint (optional)'}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none text-base sm:text-lg"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              {lang === 'zh' ? '为白板玩家提供一个提示，帮助他们理解词汇的类别' : 'Provide a hint for blank players to help them understand the word category'}
            </p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!((lang === 'zh' ? civilianWordZh : civilianWord).trim()) || !((lang === 'zh' ? undercoverWordZh : undercoverWord).trim()) || isSubmitting}
          className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-orange-700 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {isSubmitting ? (t.ui.creating || '提交中...') : (t.games.undercover.wordsmithSubmit || '提交词汇')}
        </button>
      </div>
    </div>
  );
};
